/**
 * Hook for tracking message positions for rendering lane segments.
 */

import { useState, useCallback, useEffect, RefObject } from "react";
import type { ShipmentEventApi } from "../../../../../src/shipments/types";

interface UseLanePositionsResult {
  /** Map of message index to its position and height */
  messageLanePositions: Map<number, { top: number; height: number }>;
  /** Function to update positions (call after layout changes) */
  updateLanePositions: () => void;
}

interface UseLanePositionsParams {
  messagesContainerRef: RefObject<HTMLDivElement>;
  messageRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  events: ShipmentEventApi[];
}

/**
 * Tracks positions of messages for rendering lane segments.
 * Computes exact message bounds (top + height) for each message.
 */
export function useLanePositions({
  messagesContainerRef,
  messageRefs,
  events,
}: UseLanePositionsParams): UseLanePositionsResult {
  const [messageLanePositions, setMessageLanePositions] = useState<
    Map<number, { top: number; height: number }>
  >(new Map());

  const updateLanePositions = useCallback(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const containerRect = messagesContainer.getBoundingClientRect();

    // Compute per-message positions for lane segments
    const newMsgPositions = new Map<number, { top: number; height: number }>();

    events.forEach((_, index) => {
      const msgEl = messageRefs.current.get(`msg-${index}`);
      if (msgEl) {
        const msgRect = msgEl.getBoundingClientRect();
        const top = msgRect.top - containerRect.top;
        const height = msgRect.height;
        newMsgPositions.set(index, { top, height });
      }
    });

    setMessageLanePositions(newMsgPositions);
  }, [messagesContainerRef, messageRefs, events]);

  // Update lane positions after messages render
  useEffect(() => {
    // Small delay to ensure messages are rendered
    const timer = setTimeout(updateLanePositions, 100);
    return () => clearTimeout(timer);
  }, [events, updateLanePositions]);

  return {
    messageLanePositions,
    updateLanePositions,
  };
}
